import { API_BASE as API_BASE_URL } from "./base";
import {
  appendSuperAdminReportLocationFilters,
  withSuperAdminReportLocationFilters,
} from "../utils/superAdminReportLocationFilters";

function getAuthHeaders() {
  const token = null;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function extractFilename(headers) {
  const disposition =
    headers?.get?.("content-disposition") ||
    headers?.["content-disposition"] ||
    "";
  const match = disposition.match(
    /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i,
  );
  if (!match) return "";
  const raw = match[1] || match[2] || "";
  try {
    return decodeURIComponent(raw);
  } catch (error) {
    return raw;
  }
}

function downloadBlob(blob, filename) {
  const safeName = filename || "report.csv";
  let finalBlob = blob;
  if (safeName.toLowerCase().endsWith(".csv")) {
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    finalBlob = new Blob([bom, blob], { type: "text/csv;charset=utf-8" });
  }
  const url = window.URL.createObjectURL(finalBlob);
  const link = document.createElement("a");
  link.href = url;
  link.download = safeName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

async function parseJson(res) {
  const text = await res.text();
  if (!res.ok) {
    let payload = null;
    if (text) {
      try {
        payload = JSON.parse(text);
      } catch (error) {
        payload = null;
      }
    }
    const detail =
      (payload && typeof payload.detail === "string" && payload.detail) ||
      text ||
      `Request failed with status ${res.status}`;
    const err = new Error(detail);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (error) {
    return text;
  }
}

function buildQuery(params = {}) {
  const query = appendSuperAdminReportLocationFilters(new URLSearchParams());
  if (params.scope) query.set("scope", String(params.scope));
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  if (params.include_employees != null) {
    query.set("include_employees", params.include_employees ? "true" : "false");
  }
  if (params.employee_search) {
    query.set("employee_search", String(params.employee_search));
  }
  if (params.employee_limit != null && params.employee_limit !== "") {
    query.set("employee_limit", String(params.employee_limit));
  }
  return query.toString();
}

export async function getSuperAdminCancellationsFilters(params = {}) {
  const query = buildQuery(params);
  const url = `${API_BASE_URL}/superadmin/reports/cancellations/filters${
    query ? `?${query}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminCancellationsReport(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/cancellations`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
  });
  return parseJson(res);
}

export async function exportSuperAdminCancellationsReport(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/cancellations/export`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
    },
  );
  if (!res.ok) {
    await parseJson(res);
  }
  const blob = await res.blob();
  const filename =
    extractFilename(res.headers) || "superadmin_cancellations_report.csv";
  downloadBlob(blob, filename);
}

export async function getSuperAdminClientAnalyticsFilters(params = {}) {
  const query = buildQuery(params);
  const url = `${API_BASE_URL}/superadmin/reports/client-analytics/filters${
    query ? `?${query}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminClientAnalytics(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/client-analytics`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
    },
  );
  return parseJson(res);
}

export async function getSuperAdminClientSatisfactionFilters(params = {}) {
  const query = buildQuery(params);
  const url = `${API_BASE_URL}/superadmin/reports/client-satisfaction/filters${
    query ? `?${query}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminClientSatisfactionReport(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/client-satisfaction`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
    },
  );
  return parseJson(res);
}

export async function exportSuperAdminClientSatisfactionReport(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/client-satisfaction/export`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
    },
  );
  if (!res.ok) {
    await parseJson(res);
  }
  const blob = await res.blob();
  const filename =
    extractFilename(res.headers) ||
    `superadmin_client_satisfaction_${payload?.start_date || "start"}_to_${
      payload?.end_date || "end"
    }.csv`;
  downloadBlob(blob, filename);
}

export async function getSuperAdminAppointmentStatusFilters(params = {}) {
  const query = buildQuery(params);
  const url = `${API_BASE_URL}/superadmin/reports/appointments-status/filters${
    query ? `?${query}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminAppointmentStatusReport(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/appointments-status`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
    },
  );
  return parseJson(res);
}

export async function getSuperAdminExtendedReportFilters(params = {}) {
  const query = buildQuery(params);
  const url = `${API_BASE_URL}/superadmin/reports/extended/filters${
    query ? `?${query}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminExtendedReport(reportKey, payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/extended/${encodeURIComponent(
      reportKey,
    )}/summary`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
    },
  );
  return parseJson(res);
}

export async function getSuperAdminStaffPerformanceFilters(params = {}) {
  const query = buildQuery(params);
  const url = `${API_BASE_URL}/superadmin/reports/staff-performance/filters${
    query ? `?${query}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminStaffPerformanceReport(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/staff-performance`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
    },
  );
  return parseJson(res);
}

export async function exportSuperAdminStaffPerformanceReport(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/staff-performance/export`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
    },
  );
  if (!res.ok) {
    await parseJson(res);
  }
  const blob = await res.blob();
  const filename =
    extractFilename(res.headers) ||
    `superadmin_staff_performance_${payload?.date_from || "start"}_to_${
      payload?.date_to || "end"
    }.csv`;
  downloadBlob(blob, filename);
}

export async function getSuperAdminGiftCardAnalyticsFilters(params = {}) {
  const query = appendSuperAdminReportLocationFilters(new URLSearchParams());
  if (params.scope) query.set("scope", String(params.scope));
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  const url = `${API_BASE_URL}/superadmin/reports/gift-card-analytics/filters${
    query.toString() ? `?${query.toString()}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminGiftCardAnalytics(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/gift-card-analytics`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
  });
  return parseJson(res);
}

export async function exportSuperAdminGiftCardAnalytics(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/gift-card-analytics/export`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
    },
  );
  if (!res.ok) {
    await parseJson(res);
  }
  const blob = await res.blob();
  const filename = extractFilename(res.headers) || "superadmin_gift_card_analytics.csv";
  downloadBlob(blob, filename);
}

export async function getSuperAdminRewardsFilters(params = {}) {
  const query = new URLSearchParams();
  appendSuperAdminReportLocationFilters(query);
  if (params.scope) query.set("scope", String(params.scope));
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  const url = `${API_BASE_URL}/superadmin/reports/rewards/filters${
    query.toString() ? `?${query.toString()}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminRewardsSummary(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/rewards`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
  });
  return parseJson(res);
}

export async function exportSuperAdminRewardsSummary(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/rewards/export`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
  });
  if (!res.ok) {
    await parseJson(res);
  }
  const blob = await res.blob();
  const filename =
    extractFilename(res.headers) ||
    `superadmin_rewards_analytics_${payload?.date_from || "start"}_to_${
      payload?.date_to || "end"
    }.csv`;
  downloadBlob(blob, filename);
}

export async function getSuperAdminComplaintThemesFilters() {
  const query = appendSuperAdminReportLocationFilters(new URLSearchParams());
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/complaint-themes/filters${
      query.toString() ? `?${query.toString()}` : ""
    }`,
    {
      headers: getAuthHeaders(),
    },
  );
  return parseJson(res);
}

export async function postSuperAdminComplaintThemesReport(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/complaint-themes`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
    },
  );
  return parseJson(res);
}

export async function exportSuperAdminComplaintThemesReport(payload = {}) {
  const res = await fetch(
    `${API_BASE_URL}/superadmin/reports/complaint-themes/export`,
    {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
    },
  );
  if (!res.ok) {
    await parseJson(res);
  }
  const blob = await res.blob();
  const filename =
    extractFilename(res.headers) ||
    `superadmin_complaint_themes_${payload?.start_date || "start"}_to_${
      payload?.end_date || "end"
    }.csv`;
  downloadBlob(blob, filename);
}

export async function getSuperAdminLiveDashboardFilters(params = {}) {
  const query = appendSuperAdminReportLocationFilters(new URLSearchParams());
  if (params.scope) query.set("scope", String(params.scope));
  if (params.business_id != null && params.business_id !== "") {
    query.set("business_id", String(params.business_id));
  }
  if (params.salon_id != null && params.salon_id !== "") {
    query.set("salon_id", String(params.salon_id));
  }
  const url = `${API_BASE_URL}/superadmin/reports/live-dashboard/filters${
    query.toString() ? `?${query.toString()}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function postSuperAdminLiveDashboardReport(payload = {}) {
  const res = await fetch(`${API_BASE_URL}/superadmin/reports/live-dashboard`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(withSuperAdminReportLocationFilters(payload)),
  });
  return parseJson(res);
}

function buildSuperAdminMaintenanceQuery(params = {}) {
  const query = appendSuperAdminReportLocationFilters(new URLSearchParams());
  if (params.startDate) query.set("startDate", String(params.startDate));
  if (params.endDate) query.set("endDate", String(params.endDate));
  if (params.businessId != null && params.businessId !== "") {
    query.set("businessId", String(params.businessId));
  }
  if (params.salonId != null && params.salonId !== "") {
    query.set("salonId", String(params.salonId));
  }
  if (params.staffId != null && params.staffId !== "") {
    query.set("staffId", String(params.staffId));
  }
  if (params.category) query.set("category", String(params.category));
  if (params.frequencyType) query.set("frequencyType", String(params.frequencyType));
  if (params.status) query.set("status", String(params.status));
  if (params.taskId != null && params.taskId !== "") {
    query.set("taskId", String(params.taskId));
  }
  if (params.includeInactive != null) {
    query.set("includeInactive", params.includeInactive ? "true" : "false");
  }
  if (params.bucket) query.set("bucket", String(params.bucket));
  if (params.sortBy) query.set("sortBy", String(params.sortBy));
  if (params.sortDir) query.set("sortDir", String(params.sortDir));
  if (params.page != null && params.page !== "") query.set("page", String(params.page));
  if (params.pageSize != null && params.pageSize !== "") {
    query.set("pageSize", String(params.pageSize));
  }
  return query.toString();
}

async function getSuperAdminMaintenance(path, params = {}) {
  const query = buildSuperAdminMaintenanceQuery(params);
  const url = `${API_BASE_URL}/superadmin/reports/maintenance/${path}${
    query ? `?${query}` : ""
  }`;
  const res = await fetch(url, {
    headers: getAuthHeaders(),
  });
  return parseJson(res);
}

export async function getSuperAdminMaintenanceSummary(params = {}) {
  return getSuperAdminMaintenance("summary", params);
}

export async function getSuperAdminMaintenanceTrends(params = {}) {
  return getSuperAdminMaintenance("trends", params);
}

export async function getSuperAdminMaintenanceTasks(params = {}) {
  return getSuperAdminMaintenance("tasks", params);
}

export async function getSuperAdminMaintenanceStaff(params = {}) {
  return getSuperAdminMaintenance("staff", params);
}

export async function getSuperAdminMaintenanceLogs(params = {}) {
  return getSuperAdminMaintenance("logs", params);
}

export async function getSuperAdminMaintenanceByBusiness(params = {}) {
  return getSuperAdminMaintenance("by-business", params);
}

export async function getSuperAdminMaintenanceBySalon(params = {}) {
  return getSuperAdminMaintenance("by-salon", params);
}
